import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Pause, Radio as RadioIcon, ArrowUpRight } from "lucide-react";
import { useRadio, mediaUrl } from "@/context/RadioContext";

const Bars = ({ active }) => (
  <span className="flex items-end gap-[3px] h-4" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className={`w-[3px] bg-[#E30613] ${active ? "animate-[eq_0.9s_ease-in-out_infinite]" : "h-1"}`} style={active ? { animationDelay: `${i * 0.15}s`, height: "100%" } : undefined} />
    ))}
  </span>
);

export const OnAir = () => {
  const { tracks, current, playing, playTrack, toggle, playStation } = useRadio();
  const track = tracks.find((t) => t.featured) || tracks.find((t) => t.audio_url);
  if (!track) return null;

  const isThis = current && current.id === track.id;
  const live = isThis && playing;

  const onPlay = () => {
    if (isThis) { toggle(); return; }
    if (track.audio_url) playTrack(track, tracks); else playStation();
  };

  return (
    <section id="on-air" data-testid="on-air-strip" className="relative border-y border-white/10 bg-[#070707] overflow-hidden">
      <style>{`@keyframes eq{0%,100%{transform:scaleY(.3)}50%{transform:scaleY(1)}}`}</style>
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: `url(${mediaUrl(track.artwork)})`, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(1) blur(20px)" }} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        className="relative px-5 md:px-10 py-6 md:py-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
      >
        <div className="flex items-center gap-3 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E30613] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E30613]" />
          </span>
          <span className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold text-[#E30613]"><RadioIcon size={13} /> On Air · VLMAG Radio</span>
        </div>

        <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
          <button
            onClick={onPlay}
            data-testid="on-air-play"
            aria-label={live ? "Pause" : "Play"}
            className="group relative shrink-0 w-16 h-16 md:w-20 md:h-20 border border-white/15 overflow-hidden"
          >
            {track.artwork && <img src={mediaUrl(track.artwork)} alt="" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${live ? "" : "grayscale"}`} />}
            <span className="absolute inset-0 bg-black/45 group-hover:bg-[#E30613]/80 transition-colors flex items-center justify-center">
              {live ? <Pause size={22} fill="#fff" className="text-white" /> : <Play size={22} fill="#fff" className="text-white translate-x-[2px]" />}
            </span>
          </button>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 flex items-center gap-2">
              <Bars active={live} /> {live ? "Now playing" : track.featured ? "Featured track" : "Latest drop"}
            </p>
            <p data-testid="on-air-title" className="font-display uppercase text-xl md:text-3xl leading-none tracking-tight text-white truncate mt-1.5">{track.title}</p>
            <p data-testid="on-air-artist" className="text-sm text-neutral-400 truncate mt-1">{track.artist}{track.genre ? <span className="text-neutral-600"> · {track.genre}</span> : null}</p>
          </div>
        </div>

        <Link to="/radio" data-testid="on-air-link" className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-300 hover:text-white border border-white/20 hover:border-[#E30613] px-5 py-3 transition-colors">
          Open the station <ArrowUpRight size={14} />
        </Link>
      </motion.div>
    </section>
  );
};
